"use client";

import { AdminLayout } from "@/layouts/admin-layout";
import { getActivationCodeColumns, type ActivationCodeRow } from "./columns";
import { trpc } from "@/lib/trpc";
import { authClient } from "@/lib/auth-client"; // Import authClient
import { useMemo, useState, useCallback } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { GenerateCodesDialog } from "@/components/admin/activation/GenerateCodesDialog";
import { EditCodeDialog } from "@/components/admin/activation/EditCodeDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { activationCodeStatusEnum, activationCodeSourceEnum } from "@/server/db/schema/activation";
import type { ListActivationCodesInput } from "@/types/activation";
import { Skeleton } from "@/components/ui/skeleton";

export default function AdminActivationCodesPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [filters, setFilters] = useState<Partial<ListActivationCodesInput>>({});
  const debouncedFilters = useDebounce(filters, 500);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<ActivationCodeRow | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState<ActivationCodeRow | null>(null);

  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [codeToReset, setCodeToReset] = useState<ActivationCodeRow | null>(null);

  const queryInput: ListActivationCodesInput = {
    page,
    pageSize,
    productId: debouncedFilters.productId || undefined,
    assignedEmail: debouncedFilters.assignedEmail || undefined,
    status: debouncedFilters.status || undefined,
    source: debouncedFilters.source || undefined,
  };

  const { data, isLoading, error, refetch } = trpc.activation.listActivationCodes.useQuery(
    queryInput
  );

  const { data: productsData } = trpc.activation.listProducts.useQuery({});

  const { data: session } = authClient.useSession();
  const isAdmin = useMemo(() => session?.user?.role === "admin", [session]);

  const utils = trpc.useUtils();
  const deleteCodeMutation = trpc.activation.deleteActivationCode.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.codes.deleteSuccess"));
      utils.activation.listActivationCodes.invalidate(queryInput);
      setIsDeleteDialogOpen(false);
      setCodeToDelete(null);
    },
    onError: (err) => {
      toast.error(t("admin.activation.codes.deleteError", { message: err.message }));
      setIsDeleteDialogOpen(false);
      setCodeToDelete(null);
    },
  });

  const resetCodeMutation = trpc.activation.resetActivationCode.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.codes.resetSuccess"));
      utils.activation.listActivationCodes.invalidate(queryInput);
      setIsResetDialogOpen(false);
      setCodeToReset(null);
    },
    onError: (err) => {
      toast.error(t("admin.activation.codes.resetError", { message: err.message }));
      setIsResetDialogOpen(false);
      setCodeToReset(null);
    },
  });

  const handleEdit = useCallback((code: ActivationCodeRow) => {
    setSelectedCode(code);
    setIsEditSheetOpen(true);
  }, []);

  const handleDelete = useCallback((code: ActivationCodeRow) => {
    setCodeToDelete(code);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleReset = useCallback((code: ActivationCodeRow) => {
    setCodeToReset(code);
    setIsResetDialogOpen(true);
  }, []);

  const confirmDelete = () => {
    if (codeToDelete) {
      deleteCodeMutation.mutate({ code: codeToDelete.code });
    }
  };

  const confirmReset = () => {
    if (codeToReset) {
      resetCodeMutation.mutate({ code: codeToReset.code });
    }
  };

  const columns = useMemo(
    () => getActivationCodeColumns({ onEdit: handleEdit, onDelete: handleDelete, onReset: handleReset, isAdmin, tFunction: t }),
    [handleEdit, handleDelete, handleReset, isAdmin, t]
  );

  const pageCount = data?.total ? Math.ceil(data.total / pageSize) : 0;

  const table = useReactTable({
    data: (data?.items as ActivationCodeRow[] ?? []),
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    manualPagination: true,
    state: {
      rowSelection,
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onRowSelectionChange: setRowSelection,
    pageCount,
  });

  const handleFilterChange = (key: keyof ListActivationCodesInput, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value === "all" ? undefined : value }));
    setPage(1);
  };

  // Generate loading skeleton with proper keys
  const renderLoadingSkeleton = () => {
    // Generate stable unique IDs for skeleton rows
    return Array.from({ length: pageSize }).map((_, rowIndex) => {
      // Create a stable ID for this row based on page and rowIndex
      const stableRowId = `skeleton-${page}-${rowIndex}`;

      return (
        <TableRow key={stableRowId}>
          {/* Code column */}
          <TableCell key={`${stableRowId}-code`} className="py-3">
            <Skeleton className="h-5 w-24 rounded font-mono" />
          </TableCell>

          {/* Product column */}
          <TableCell key={`${stableRowId}-product`} className="py-3">
            <Skeleton className="h-5 w-32 rounded" />
          </TableCell>

          {/* Source column */}
          <TableCell key={`${stableRowId}-source`} className="py-3">
            <Skeleton className="h-6 w-20 rounded-full" />
          </TableCell>

          {/* Assigned Email column */}
          <TableCell key={`${stableRowId}-email`} className="py-3">
            <Skeleton className="h-5 w-40 rounded" />
          </TableCell>

          {/* Status column */}
          <TableCell key={`${stableRowId}-status`} className="py-3">
            <Skeleton className="h-6 w-20 rounded-full" />
          </TableCell>

          {/* Expires At column */}
          <TableCell key={`${stableRowId}-expires`} className="py-3">
            <Skeleton className="h-5 w-24 rounded" />
          </TableCell>

          {/* Created At column */}
          <TableCell key={`${stableRowId}-created`} className="py-3">
            <Skeleton className="h-5 w-24 rounded" />
          </TableCell>

          {/* Actions column */}
          <TableCell key={`${stableRowId}-actions`} className="py-3 text-right">
            <div className="flex justify-end">
              <Skeleton className="h-8 w-8 rounded ml-auto" />
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  if (error) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-10">
          <p>{t("common.errorPrefix")}: {error.message}</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t("admin.activation.codes.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("admin.activation.codes.description", "Manage activation codes for products.")}</p>
          </div>
          <GenerateCodesDialog onCodesGenerated={refetch} />
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <Input
            placeholder={t("admin.users.page.searchByPlaceholder", {field: t("admin.activation.codes.assignedEmailLabel").toLowerCase()})}
            value={filters.assignedEmail ?? ""}
            onChange={(e) => handleFilterChange("assignedEmail", e.target.value)}
            className="w-full sm:w-72"
          />
          <Select
            value={filters.productId ?? "all"}
            onValueChange={(value) => handleFilterChange("productId", value)}
          >
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue placeholder={t("admin.activation.codes.selectProductPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {productsData?.items?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.status ?? "all"}
            onValueChange={(value) => handleFilterChange("status", value)}
          >
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue placeholder={t("admin.activation.codes.selectStatusPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {activationCodeStatusEnum.enumValues.map(s => <SelectItem key={s} value={s}>{t(`admin.activationCodeStatus.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.source ?? "all"}
            onValueChange={(value) => handleFilterChange("source", value)}
          >
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue placeholder={t("admin.activation.codes.selectSourcePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {activationCodeSourceEnum.enumValues.map(s => <SelectItem key={s} value={s}>{t(`admin.activationCodeSource.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-6">
          <div className="bg-card rounded-md border shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="bg-muted/50">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className="font-medium py-4">
                        {header.isPlaceholder ? null : (
                          <div>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  renderLoadingSkeleton()
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="hover:bg-muted/50"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {t("admin.activation.codes.noResults")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between px-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} {t("common.of")} {table.getFilteredRowModel().rows.length} {t("common.rowsSelected")}
            </div>
            <div className="flex items-center space-x-6 lg:space-x-8">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">
                  {t("common.rowsPerPage")}
                </span>
                <Select
                  value={`${pageSize}`}
                  onValueChange={(value) => {
                    const newPageSize = Number(value);
                    setPageSize(newPageSize);
                  }}
                >
                  <SelectTrigger className="h-8 w-20">
                    <SelectValue placeholder={pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 20, 30, 40, 50].map((size) => (
                      <SelectItem key={size} value={`${size}`}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-[120px] items-center justify-center text-sm font-medium">
                {t("common.page")} {page} {t("common.of")} {pageCount || 1}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  <span className="sr-only">{t("common.firstPage")}</span>
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <span className="sr-only">{t("common.previousPage")}</span>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pageCount}
                >
                  <span className="sr-only">{t("common.nextPage")}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => setPage(pageCount)}
                  disabled={page === pageCount}
                >
                  <span className="sr-only">{t("common.lastPage")}</span>
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {selectedCode && (
          <EditCodeDialog
            activationCode={{
              ...selectedCode,
              expiresAt: new Date(selectedCode.expiresAt),
              activatedAt: selectedCode.activatedAt ? new Date(selectedCode.activatedAt) : null,
              createdAt: new Date(selectedCode.createdAt),
              updatedAt: new Date(selectedCode.updatedAt),
            }}
            isOpen={isEditSheetOpen}
            onOpenChange={setIsEditSheetOpen}
            onCodeUpdated={() => {
              refetch();
              setSelectedCode(null);
              setIsEditSheetOpen(false);
            }}
          />
        )}

        {codeToDelete && (
          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("admin.activation.codes.deleteConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("admin.activation.codes.deleteConfirmDescription", { code: codeToDelete.code })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setCodeToDelete(null)}>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={confirmDelete} disabled={deleteCodeMutation.isPending}>
                  {deleteCodeMutation.isPending ? t("common.deleting") : t("settings.apiKeys.actions.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {codeToReset && (
          <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("admin.activation.codes.resetConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("admin.activation.codes.resetConfirmDescription", { code: codeToReset.code })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setCodeToReset(null)}>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={confirmReset} disabled={resetCodeMutation.isPending}>
                  {resetCodeMutation.isPending ? t("common.resetting") : t("admin.activation.codes.actions.reset")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </AdminLayout>
  );
}